import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import utils from "../utils/utils";
import {
  extractSuccessStory,
  firstSuccessStoryRow,
  isProcFailure,
  isProcSuccess,
} from "./dmsMasterService";

function getApiDataset(response) {
  if (!response || typeof response !== "object") return null;
  const d = response.data ?? response.Data;
  return d && typeof d === "object" ? d : null;
}

function getNamedTable(response, tableName) {
  const ds = getApiDataset(response);
  if (!ds) return [];
  if (Array.isArray(ds[tableName])) return ds[tableName];
  const key = Object.keys(ds).find(
    (k) => k.toLowerCase() === String(tableName).toLowerCase(),
  );
  if (key && Array.isArray(ds[key])) return ds[key];
  return [];
}

function resolveMessage(response, row, fallback) {
  return (
    row?.MSG ||
    row?.Msg ||
    row?.message ||
    row?.Message ||
    utils.getApiResponseMessage?.(response, fallback) ||
    response?.message ||
    fallback
  );
}

async function postDepartmentManager(payload) {
  const response = await api.post(
    API_ENDPOINTS.SETTINGS.DEPARTMENT_MANAGER,
    payload,
  );
  return response;
}

export async function fetchDepartments(userData) {
  const response = await postDepartmentManager({
    OrganizationId: userData.OrganizationId,
    CreatedBy: userData.StaffId,
    ActionType: "GetByOrganization",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Failed to load departments."),
    );
  }
  return getNamedTable(response, "DepartmentList");
}

export async function saveDepartment(userData, { departmentId, departmentName }) {
  const isEdit = Boolean(departmentId);
  const response = await postDepartmentManager({
    OrganizationId: userData.OrganizationId,
    CreatedBy: userData.StaffId,
    DepartmentId: departmentId || "",
    DepartmentName: departmentName,
    ActionType: isEdit ? "Update" : "Create",
  });
  const row = firstSuccessStoryRow(response);
  if (isProcFailure(row) || (!isProcSuccess(row) && !utils.isApiSuccess(response))) {
    throw new Error(resolveMessage(response, row, "Failed to save department."));
  }
  return {
    message: resolveMessage(
      response,
      row,
      isEdit ? "Department updated." : "Department saved.",
    ),
  };
}

export async function softDeleteDepartment(userData, departmentId) {
  const response = await postDepartmentManager({
    OrganizationId: userData.OrganizationId,
    CreatedBy: userData.StaffId,
    DepartmentId: departmentId,
    ActionType: "SoftDelete",
  });
  const row = firstSuccessStoryRow(response);
  if (isProcFailure(row) || (!isProcSuccess(row) && !utils.isApiSuccess(response))) {
    throw new Error(
      resolveMessage(response, row, "Failed to delete department."),
    );
  }
  return {
    message: resolveMessage(response, row, "Department deleted."),
  };
}

export async function activateDepartment(userData, departmentId) {
  const response = await postDepartmentManager({
    OrganizationId: userData.OrganizationId,
    CreatedBy: userData.StaffId,
    DepartmentId: departmentId,
    ActionType: "Activate",
  });
  const row = firstSuccessStoryRow(response);
  if (isProcFailure(row) || (!isProcSuccess(row) && !utils.isApiSuccess(response))) {
    throw new Error(
      resolveMessage(response, row, "Failed to activate department."),
    );
  }
  return {
    message: resolveMessage(response, row, "Department activated."),
  };
}

export async function fetchDepartmentLog(userData, departmentId = "") {
  const response = await postDepartmentManager({
    OrganizationId: userData.OrganizationId,
    CreatedBy: userData.StaffId,
    DepartmentId: departmentId || "",
    ActionType: "GetLog",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Failed to load department log."),
    );
  }
  const named = getNamedTable(response, "DepartmentLog");
  if (named.length) return named;
  return extractSuccessStory(response, "DepartmentLog");
}
